import { z } from 'zod';
import { paginated, paginationQuerySchema } from './common.js';
// ── which providers run outside the platform ────────────────────────────────
/**
 * The providers that run INSIDE the platform, and therefore need no consent.
 *
 * ⚠️ **AN INTERNAL LIST, NOT AN EXTERNAL ONE, and the polarity is the whole point.**
 * `isExternalLlmProvider` treats anything unlisted as external, so a provider added
 * to the gateway and forgotten here is reported as sending data out. That is the
 * SAFE direction: the alternative — an allow-list of external providers — would
 * report a new external provider as internal, and a customer would be told their
 * data stays put while it leaves. Over-claiming egress is an annoyance; under-claiming
 * it is the one mistake this whole mechanism exists to prevent.
 *
 * ⚠️ **It is a SECOND copy of a fact `@oip/llm-gateway` already declares** — each
 * provider carries `capabilities.external` — and it exists because
 * `check:layering` forbids `apps/api` importing the gateway, so the API cannot ask.
 * `apps/worker` sees both and holds the parity guard.
 */
export const INTERNAL_LLM_PROVIDERS = ['mock', 'local'];
/** True when a model call to this provider would leave the platform. */
export function isExternalLlmProvider(providerId) {
    return !INTERNAL_LLM_PROVIDERS.includes(providerId);
}
/**
 * ⚠️ The gateway's own fallbacks, restated because it reads raw `process.env` rather
 * than a validated schema — so an unset variable means `mock`, and a screen that
 * defaulted differently would name a provider no call would reach. Held in step by
 * the same parity guard.
 */
export const LLM_PROVIDER_DEFAULTS = { provider: 'mock', model: 'mock-deterministic-1' };
// ── prompt registry (read-only) ─────────────────────────────────────────────
export const llmPromptVersionSchema = z.object({
    id: z.string().uuid(),
    version: z.string(),
    status: z.string(),
    schemaVersion: z.string(),
    /**
     * The prompt itself.
     *
     * Withheld originally, which left the registry listing metadata about
     * templates nobody could read — a screen whose whole purpose is showing what
     * the wording layer does, showing everything except that. These carry no
     * credentials and no tenant data: they are instructions for rewording output
     * the deterministic engine has already decided, and the system prompt's own
     * first line is "You are a wording assistant. You NEVER make decisions."
     * Being able to read that is the point.
     */
    systemTemplate: z.string(),
    userTemplate: z.string(),
    providerVariant: z.string().nullable(),
    modelVariant: z.string().nullable(),
    createdAt: z.string(),
    activatedAt: z.string().nullable(),
    retiredAt: z.string().nullable(),
});
export const llmPromptSchema = z.object({
    id: z.string().uuid(),
    taskType: z.string(),
    promptKey: z.string(),
    versions: z.array(llmPromptVersionSchema),
    /**
     * How many executions this prompt has produced, platform-wide.
     *
     * Null on the tenant-scoped route, and deliberately so. A prompt has no
     * tenant, so the only honest count is the global one — and handing a tenant a
     * global volume figure leaks a little about other customers for no benefit.
     * Reporting their own count instead would give one field two meanings, which
     * is how a number ends up quietly wrong.
     *
     * It matters because it is the evidence behind "this prompt is never used":
     * two of the three registered tasks have no caller anywhere.
     */
    executionCount: z.number().int().nullable(),
});
export const llmPromptsResponseSchema = z.object({ prompts: z.array(llmPromptSchema) });
// ── executions ──────────────────────────────────────────────────────────────
export const llmExecutionListQuerySchema = paginationQuerySchema
    .extend({
    sort: z.enum(['createdAt', 'status']).default('createdAt'),
    taskType: z.string().optional(),
    status: z.string().optional(),
    provider: z.string().optional(),
    recommendationId: z.string().uuid().optional(),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
})
    .strict();
export const llmExecutionListItemSchema = z.object({
    id: z.string().uuid(),
    taskType: z.string(),
    provider: z.string(),
    model: z.string(),
    status: z.string(),
    outputSource: z.string().nullable(),
    fallbackReason: z.string().nullable(),
    recommendationId: z.string().uuid().nullable(),
    promptVersionId: z.string().uuid().nullable(),
    attemptCount: z.number().int(),
    latencyMs: z.number().int().nullable(),
    createdAt: z.string(),
});
export const llmExecutionListResponseSchema = paginated(llmExecutionListItemSchema);
export const llmExecutionDetailSchema = llmExecutionListItemSchema.extend({
    schemaVersion: z.string().nullable(),
    inputFingerprint: z.string().nullable(),
    outputFingerprint: z.string().nullable(),
    tokenUsage: z.record(z.unknown()).nullable(),
    errorCategory: z.string().nullable(),
    errorMessage: z.string().nullable(),
    correlationId: z.string().nullable(),
    completedAt: z.string().nullable(),
});
/** Explanation history for a recommendation = its executions, newest first. */
export const llmExplanationHistoryResponseSchema = z.object({
    recommendationId: z.string().uuid(),
    currentSource: z.string(),
    executions: z.array(llmExecutionListItemSchema),
});
// ── regenerate ────────────────────────────────────────────────────────────
export const regenerateExplanationResponseSchema = z.object({
    recommendationId: z.string().uuid(),
    status: z.literal('QUEUED'),
    note: z.string(),
});
// ── prompt authoring (platform administrators only) ─────────────────────────
/**
 * What a prompt template may substitute, per task type.
 *
 * `renderTemplate` replaces an unrecognised `{placeholder}` with the literal
 * string **"unknown"** (`domain/recommendation/generator.ts`). So a typo does
 * not fail — it sends the model a sentence containing the word "unknown" and
 * the model writes confident prose around it. Nothing errors and nothing logs,
 * which is the same shape as the free-text `eventType` and the unvalidated
 * policy field path this codebase has already fixed twice.
 *
 * Derived from `buildPromptContext` in `@oip/llm-gateway`, which is a single
 * object literal, and kept in step by a test that calls the real function.
 *
 * ⚠️ **FLEET_BRIEFING WAS OMITTED ON A FALSE PREMISE.** This said "nothing in the
 * platform executes them — there is no context builder to derive from", and for
 * the briefing both halves were wrong: `scheduled-reports` calls
 * `narrateBriefing` as the wording step for every briefing report, and it built
 * a context inline. Being inline rather than named is what made it look absent,
 * and the registry then showed a WARNING on a prompt that genuinely runs — a
 * working path reported as dead, which is the worse direction of the two.
 *
 * **SUMMARISE_INCIDENT is still absent, and that one is correct.** It has a
 * prompt, an output schema, routing config and a fallback, and nothing calls it.
 * There is no context builder because there is no execution, so inventing a list
 * would be guesswork presented as a rule; its placeholders stay unconstrained
 * and the screen says the task is not wired to anything.
 */
export const PROMPT_VARIABLES = {
    /*
     * The scheduled report's briefing wording. Derived from
     * `buildBriefingPromptContext`, extracted for exactly this and held in step by
     * the same parity test — the figures are already computed, and the model is
     * only ever asked to reword them (D5).
     */
    /*
     * The incident summary, asked for ON DEMAND from the incident page rather than raised
     * automatically — see `summariseIncident`. Derived from
     * `buildIncidentSummaryPromptContext` and held in step by the same parity test.
     */
    SUMMARISE_INCIDENT: [
        'evidence',
        'incident.status',
        'incident.summary',
        'incident.title',
        'incident.type',
        'risk.level',
    ],
    FLEET_BRIEFING: [
        'attentionItems',
        'criticalIncidents',
        'newRecommendations',
        'openIncidents',
        'period',
        'periodEnd',
        'periodStart',
    ],
    /*
     * The copilot's wording task. Derived from `buildCopilotPromptContext`, and a
     * parity test in `llm-gateway` keeps the two in step — `renderTemplate` turns
     * an unrecognised `{placeholder}` into the literal word "unknown", so a typo
     * would otherwise reach the model in silence.
     */
    COPILOT_ANSWER: [
        'capability',
        'coverage',
        'evidence',
        'figures',
        'headline',
        'narrative',
        'question',
        'scope',
    ],
    /*
     * The copilot's ROUTING task. Derived from `buildCopilotRoutePromptContext`,
     * parity-tested in `llm-gateway`. Two variables only, and deliberately: the
     * model is shown the declared questions and the reader's sentence, and nothing
     * else. A third variable would be a third thing it could reason from, and G1
     * is the rule that it reasons from a list.
     */
    COPILOT_ROUTE: ['catalogue', 'choices', 'question'],
    EXPLAIN_RECOMMENDATION: [
        'action.approvalRequired',
        'action.category',
        'action.recommended',
        'contributingEvents',
        'deterministicExplanation',
        'evidence',
        'incident.summary',
        'incident.title',
        'incident.type',
        'policy.name',
        'policy.version',
        'reasoning',
        'risk.level',
        'risk.score',
        'uncertaintyPreserved',
        'vehicle.name',
        'vehicle.registration',
    ],
};
/** True when the platform actually executes this task type. */
export function promptTaskIsWired(taskType) {
    return taskType in PROMPT_VARIABLES;
}
/**
 * Placeholders in these templates that the runtime will not supply.
 *
 * Empty for a task type with no execution path: there is nothing to check
 * against, and refusing on a guess would be worse than allowing it and saying
 * the task is inert.
 */
export function unknownPromptVariables(taskType, ...templates) {
    const known = PROMPT_VARIABLES[taskType];
    if (!known)
        return [];
    const found = new Set();
    for (const t of templates) {
        // The same pattern `renderTemplate` uses, so what is checked is what is
        // substituted — a narrower pattern here would miss real substitutions.
        for (const m of t.matchAll(/\{([a-zA-Z0-9_.]+)\}/g))
            found.add(m[1]);
    }
    return [...found].filter((v) => !known.includes(v)).sort();
}
export const createPromptVersionRequestSchema = z
    .object({
    version: z.string().trim().min(1).max(40),
    systemTemplate: z.string().trim().min(1).max(20000),
    userTemplate: z.string().trim().min(1).max(20000),
    schemaVersion: z.string().trim().min(1).max(40).optional(),
    providerVariant: z.string().trim().min(1).max(60).nullable().optional(),
    modelVariant: z.string().trim().min(1).max(60).nullable().optional(),
})
    .strict();
export const updatePromptVersionRequestSchema = z
    .object({
    systemTemplate: z.string().trim().min(1).max(20000),
    userTemplate: z.string().trim().min(1).max(20000),
})
    .strict();
/**
 * The task types the gateway knows how to run.
 *
 * Mirrors `LLM_TASK_TYPES` in `@oip/llm-gateway`; the packages deliberately do
 * not depend on each other, and `prompt-variables.test.ts` keeps them in step.
 * It is an enum on write for the same reason `eventType` is: a prompt
 * registered under a task nothing asks for is inert, and inert-but-configured
 * is the failure this codebase keeps finding.
 */
export const LLM_TASK_TYPES = [
    'EXPLAIN_RECOMMENDATION',
    'SUMMARISE_INCIDENT',
    'FLEET_BRIEFING',
    'COPILOT_ANSWER',
    'COPILOT_ROUTE',
];
/**
 * Register a prompt under a NON-DEFAULT key, for a tenant to be pointed at.
 *
 * `default` is excluded deliberately: one already exists per task type, and the
 * gateway falls back to it. Creating a second `default` is not a thing anybody
 * wants; creating `northwind` is.
 */
export const createPromptRequestSchema = z
    .object({
    taskType: z.enum(LLM_TASK_TYPES),
    promptKey: z
        .string()
        .trim()
        .min(2)
        .max(40)
        .regex(/^[a-z0-9][a-z0-9-]*$/, 'Lower-case letters, digits and hyphens')
        .refine((k) => k !== 'default', 'A default prompt already exists for every task type'),
    /**
     * Start it from the platform default's active wording rather than empty.
     *
     * An override almost always begins as "the standard brief, but ours" — and
     * a blank prompt is not a starting point, it is a blank page. Copied as a
     * DRAFT, so registering a key still changes nothing until it is activated
     * AND a tenant is pointed at it.
     */
    cloneFromDefault: z.boolean().optional(),
})
    .strict();
/**
 * Point a tenant at a prompt key, or back at the platform default.
 *
 * Writes `llm.promptKey.{taskType}`, which `execution.service` already reads —
 * `gate.settings?.promptKeyOverride ?? route.promptKey`. Null clears it.
 */
export const promptKeyOverrideRequestSchema = z
    .object({
    taskType: z.enum(LLM_TASK_TYPES),
    promptKey: z.string().trim().min(2).max(40).nullable(),
})
    .strict();
// ── AI usage and allowance ──────────────────────────────────────────────────
/**
 * What the organisation has spent, against what it is allowed.
 *
 * The budget was enforceable and invisible: `aiBudgetStatus` decided in the
 * worker and no route read it, so the administrator who set a ceiling could not
 * see how close anyone was to it — the first sign was a finding degrading, which
 * reports the limit only after it has already bitten.
 *
 * **A null limit is UNLIMITED, not zero**, and the card must say so in words. A
 * screen showing "0" against a limit nobody set would read as an exhausted
 * allowance, which is the opposite of the truth.
 */
export const aiAllowanceSchema = z.object({
    /** Tokens spent this calendar month across the whole organisation. */
    monthTokens: z.number().int(),
    monthLimit: z.number().int().nullable(),
    /** Tokens spent today across the whole organisation. */
    dayTokens: z.number().int(),
    dayLimit: z.number().int().nullable(),
    /** Model calls THIS user has made in the last hour. */
    userHourRequests: z.number().int(),
    userHourLimit: z.number().int().nullable(),
    /**
     * When each window rolls over, so the card can say "resets at" rather than
     * leaving the reader to work out what "monthly" means for their calendar.
     */
    monthResetsAt: z.string(),
    dayResetsAt: z.string(),
    hourResetsAt: z.string(),
    /**
     * The tenant that OWNS the allowance — the parent.
     *
     * Named because the pool is organisation-wide (D8): a branch administrator
     * seeing usage they did not generate needs to know it is the organisation's
     * figure, not a fault in their own.
     */
    ownerTenantId: z.string().uuid(),
    /** True when no limit is configured at all — the common case. */
    unlimited: z.boolean(),
});
/**
 * Setting the organisation's AI allowance — PLATFORM-scoped.
 *
 * Deliberately not on Tenant Settings. The allowance is PARENT-owned and
 * organisation-wide, so a field reachable from inside a branch would let a
 * branch administrator raise the whole customer's ceiling from a page that never
 * says whose ceiling it is. The platform tenant screens operate on a NAMED
 * tenant from outside it, which is the only place this can be edited honestly.
 *
 * **An omitted field is left unchanged; an explicit `null` removes the limit.**
 * The two must be distinguishable: "I did not touch the monthly cap" and "the
 * monthly cap should be unlimited" are different intentions, and a schema that
 * collapsed them would silently clear a ceiling somebody meant to keep.
 */
export const updateAiBudgetSchema = z
    .object({
    monthlyTokens: z.number().int().positive().nullable().optional(),
    dailyTokens: z.number().int().positive().nullable().optional(),
    userHourlyRequests: z.number().int().positive().nullable().optional(),
})
    .strict();
/**
 * A tenant's allowance and what it has spent, for the platform screen.
 *
 * Carries usage as well as limits so the person setting a ceiling can see what
 * they are capping. Setting a limit blind is how you either strangle a customer
 * or write a number that never binds.
 */
export const tenantAiBudgetResponseSchema = z.object({
    tenantId: z.string().uuid(),
    tenantName: z.string(),
    limits: z.object({
        monthlyTokens: z.number().int().nullable(),
        dailyTokens: z.number().int().nullable(),
        userHourlyRequests: z.number().int().nullable(),
    }),
    /** Billable tokens only — mock and local providers bill nothing. */
    monthTokens: z.number().int(),
    dayTokens: z.number().int(),
});
//# sourceMappingURL=llm.js.map